import {StudioCheckbox} from '../../base-components/StudioCheckbox';
import {StudioSelect} from '../../base-components/StudioSelect';
import {InferenceAppFieldsBaseProps, InferenceAppForm} from './InferenceAppTypes';
import React, {useEffect, useMemo} from 'react';
import {Field, FieldProps} from 'formik';
import {useIntl} from 'react-intl';
import {Alert} from '@material-ui/lab';
import {Button} from '@material-ui/core';
import {Link} from 'react-router-dom';
import {DeploymentPostProcessingParameters} from '../TestDeployView/DeployConfigPanel/TestDeployParameters';
import {RUNTIME_MODE_OPTIONS} from '../TestDeployView/useDeploy';
import {getApplicationType} from '../../types/model/framework/ApplicationType';

export type InferenceAppFieldsProps = InferenceAppFieldsBaseProps & {
  allowAPIExposure: boolean;
  postProcessorRequired: boolean;
};

export const InferenceAppConfigFields = ({
  application,
  inProgress,
  allowAPIExposure,
  values,
  spa,
  postProcessorRequired,
  projectPostProcessors,
  viewMode,
  handlePostProcessorChange,
  onCheckboxChanged,
  setReady,
  gspList,
}: InferenceAppFieldsProps) => {
  const intl = useIntl();

  const projectPostProcessorOptions =
    projectPostProcessors.reduce((acc, curr) => ({...acc, [curr.id]: curr.name}), {}) ||
    {};

  const modelPurpose = application.model?.modelPurpose;

  const enableDefaultPostProcessor =
    (application.runtimeType === 'OPENVX' ||
      application.runtimeType === 'PY_PICASSO' ||
      application.runtimeType === 'OPENCV_PYTHON') &&
    (modelPurpose === 'Classification' ||
      modelPurpose === 'Detection' ||
      modelPurpose === 'Other');

  const hasPostProcessors = projectPostProcessors.length > 0;

  const checkReadiness = () => {
    setReady(
      (!viewMode ||
        (values.exposeInferencePage &&
          (!postProcessorRequired || !!values.postProcessorId))) &&
        (application.runtimeType !== 'ONNX_RUNTIME_WEB' || !!values.runtimeMode)
    );
  };

  useEffect(() => {
    checkReadiness();
  });

  const applicationType = getApplicationType(application.runtimeType);
  const deploymentId = spa.target.deploymentId ? spa.target.deploymentId : undefined;

  const postprocessorIsInUse = useMemo(
    () =>
      (application?.postProcessors || []).find(pp => pp.id === values.postProcessorId),
    [application?.postProcessors]
  );

  return (
    <>
      {!viewMode && (
        <div>
          {allowAPIExposure && (
            <Field name="exposeAPI" type="checkbox">
              {({field}: FieldProps<InferenceAppForm['exposeAPI']>) => (
                <StudioCheckbox
                  name="exposeAPI"
                  label={intl.formatMessage({id: 'inferenceApp.exposeAPI'})}
                  checked={field.value}
                  onChange={event => {
                    field.onChange(event);
                    if (onCheckboxChanged) {
                      onCheckboxChanged(field, event);
                    }
                    checkReadiness();
                  }}
                  disabled={inProgress || application.runtimeType === 'ONNX_RUNTIME_WEB'}
                />
              )}
            </Field>
          )}
          <Field name="exposeInferencePage">
            {({field}: FieldProps<InferenceAppForm['exposeInferencePage']>) => (
              <StudioCheckbox
                name="exposeInferencePage"
                label={intl.formatMessage({
                  id: 'inferenceApp.exposeInferencePage',
                })}
                checked={field.value}
                onChange={event => {
                  field.onChange(event);
                  if (onCheckboxChanged) {
                    onCheckboxChanged(field, event);
                  }
                  checkReadiness();
                }}
                disabled={inProgress}
              />
            )}
          </Field>
        </div>
      )}
      {values.exposeInferencePage && postProcessorRequired && (
        <Field name="postProcessorId">
          {({field}: FieldProps<InferenceAppForm['postProcessorId']>) => (
            <div>
              {hasPostProcessors && (
                <StudioSelect
                  label={intl.formatMessage({
                    id: 'inferenceApp.postProcessor',
                  })}
                  options={projectPostProcessorOptions}
                  SelectProps={{
                    ...field,
                    value: field.value || '',
                    onChange: e => {
                      field.onChange(e);
                      if (handlePostProcessorChange) {
                        handlePostProcessorChange(e);
                      }
                      checkReadiness();
                    },
                  }}
                  fullWidth
                  disabled={inProgress || viewMode}
                  placeholder="Select a post processor"
                  disablePlaceholder
                />
              )}
              <div className="inference-app__fields__buttons">
                {!viewMode && enableDefaultPostProcessor && !hasPostProcessors && (
                  <>
                    <Alert severity="warning" className="dplmt-config-field__alert">
                      {intl.formatMessage({
                        id: 'test.runtime.postprocessor.warning',
                      })}
                    </Alert>
                    <Button color="primary">
                      <Link
                        to={`/project/${
                          application.projectId
                        }/postprocessor/new?applicationId=${
                          application.id
                        }&modelPurpose=${modelPurpose}&appType=${applicationType}&useDefault=${modelPurpose}&returnTo=spaCreate&spaId=${spa.id ||
                          ''}${deploymentId ? '&deploymentId=' + deploymentId : ''}`}
                      >
                        {intl.formatMessage({
                          id: 'inferenceApp.postProcessor.create.default',
                        })}
                      </Link>
                    </Button>
                  </>
                )}
                {hasPostProcessors && (
                  <Button color="primary" disabled={field.value == null}>
                    {!viewMode && !postprocessorIsInUse ? (
                      <Link
                        to={`/project/${application.projectId}/postprocessor/edit/${
                          field.value
                        }?modelPurpose=${modelPurpose}&applicationId=${
                          application.id
                        }&returnTo=spaCreate&spaId=${spa.id || ''}${
                          deploymentId ? '&deploymentId=' + deploymentId : ''
                        }`}
                      >
                        {intl.formatMessage({
                          id: 'inferenceApp.postProcessor.view.edit',
                        })}
                      </Link>
                    ) : (
                      <Link
                        to={`/project/${application.projectId}/postprocessor/edit/${
                          field.value
                        }?modelPurpose=${modelPurpose}&applicationId=${
                          application.id
                        }&returnTo=spaCreate&spaId=${spa.id || ''}${
                          deploymentId ? '&deploymentId=' + deploymentId : ''
                        }&viewOnly=true`}
                      >
                        {intl.formatMessage({
                          id: 'inferenceApp.postProcessor.view',
                        })}
                      </Link>
                    )}
                  </Button>
                )}
                {!viewMode && (
                  <Button color="primary">
                    <Link
                      to={`/project/${
                        application.projectId
                      }/postprocessor/new?modelPurpose=${modelPurpose}&applicationId=${
                        application.id
                      }&returnTo=spaCreate&&spaId=${spa.id || ''}${
                        deploymentId ? '&deploymentId=' + deploymentId : ''
                      }`}
                    >
                      {enableDefaultPostProcessor
                        ? intl.formatMessage({
                            id: 'inferenceApp.postProcessor.create.custom',
                          })
                        : intl.formatMessage({
                            id: 'inferenceApp.postProcessor.create.new',
                          })}
                    </Link>
                  </Button>
                )}
              </div>
            </div>
          )}
        </Field>
      )}
      {values.exposeInferencePage && (
        <>
          {!viewMode && (
            <div className="inference-app__params-container">
              <Field name="postProcessorParams">
                {({form}: FieldProps<InferenceAppForm['postProcessorParams']>) => (
                  <DeploymentPostProcessingParameters
                    projectId={application.projectId}
                    applicationId={application.id}
                    spaId={spa.id}
                    renderField={(field, param) => (
                      <div className="inference-app__param" key={param.id}>
                        {field}
                      </div>
                    )}
                    titleMessageId="test.form.postprocessing"
                    wrapperClassName="inference-app__accordion"
                    detailsClassName="deploy-settings__body_container"
                    onValidityChange={isValid => {
                      if (!isValid) {
                        form.setFieldError(
                          'postProcessorParams',
                          'Invalid post processing parameters'
                        );
                      }
                    }}
                    onChange={newParams => {
                      form.setFieldValue('postProcessorParams', newParams);
                      checkReadiness();
                    }}
                    disabledForm={inProgress}
                    persistChanges={true}
                    executionContainer={'BROWSER'}
                    postProcessorType={'APPLICATION'}
                  />
                )}
              </Field>
            </div>
          )}
          {application.runtimeType === 'ONNX_RUNTIME_WEB' && (
            <Field name="runtimeMode">
              {({field}: FieldProps<InferenceAppForm['runtimeMode']>) => (
                <StudioSelect
                  id="runtime-mode"
                  required
                  fullWidth
                  disabled={inProgress}
                  label={intl.formatMessage({id: 'test.runtime.label'})}
                  options={RUNTIME_MODE_OPTIONS}
                  SelectProps={field}
                  tooltip={intl.formatMessage({id: 'test.runtime.tooltip'})}
                  onChange={event => {
                    field.onChange(event);
                    checkReadiness();
                  }}
                />
              )}
            </Field>
          )}
          {/* Let user choose target device in "edit" mode for test SPA if server allows that choice for this application*/}
          {!viewMode && !deploymentId && gspList && gspList.length > 0 && (
            <Field name="schedulerId">
              {({field}: FieldProps<InferenceAppForm['schedulerId']>) => (
                <StudioSelect
                  data-testid="target-select"
                  fullWidth
                  label={intl.formatMessage({id: 'test.form.availableDevice'})}
                  options={gspList.reduce(
                    (options, item) => ({
                      ...options,
                      [item.id]: item.name,
                    }),
                    {}
                  )}
                  placeholder={intl.formatMessage({id: 'test.selectDevice'})}
                  disablePlaceholder
                  onChange={event => {
                    field.onChange(event);
                    checkReadiness();
                  }}
                />
              )}
            </Field>
          )}
        </>
      )}
    </>
  );
};
